<script>
  import { onMount } from 'svelte';
  import { get } from 'svelte/store';
  import { format } from 'svelte-i18n';

  import { getNotificationsContext } from '@client/components/notifications';
  import { Section, SectionItem } from '@client/components/section';
  import { Select } from '@client/components/select';

  import { BizCardsService } from '@client/services/bizcards';

  import { id, loadBizcard, updateBizcard, updatedRulename } from '@client/stores/bizcard';

  import StudioForm from './shared/studio-form.svelte';

  const { addNotification } = getNotificationsContext();

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  let selectedBizcard = null;
  let bizcardItems = [];
  let originalBizcard = null;

  const handleBizcardSelect = (event) => {
    selectedBizcard = event.detail;
  };

  const loadBizcardItem = async (item) => {
    if (item) {
      await loadBizcard(item.value);
      // Save the complete original bizcard for rollback
      originalBizcard = await BizCardsService.getById(item.value);
    }
  };

  const executeUpdateBizcard = async () => {
    const success = await updateBizcard();

    if (!success) {
      throw new Error($format('label.FAILURE.UPDATE_BUSINESS_CARD'));
    }

    const bizcardName = get(updatedRulename);

    step.data = {
      id: bizcardName,
      originalBizcard: originalBizcard
    };

    return { rulename: bizcardName };
  };

  const rollbackUpdateBizcard = async () => {
    if (!step.data?.originalBizcard) {
      return;
    }

    try {
      // Restore the complete original bizcard state
      await BizCardsService.update(step.data.originalBizcard);

      addNotification({
        text: $format('label.SUCCESS.BUSINESS_CARD_UPDATE'),
        type: 'success',
        autohide: 2500,
        position: 'top-right'
      });
    } catch (error) {
      console.error('Failed to rollback bizcard update:', error);
      addNotification({
        text: $format('label.FAILURE.BUSINESS_CARD_UPDATE'),
        type: 'error',
        autohide: 2500,
        position: 'top-right'
      });
      throw error;
    }
  };

  onMount(async () => {
    // Load all bizcards for the select
    const allBizcards = await BizCardsService.getAll();
    bizcardItems = allBizcards.map((bizcard) => ({
      label: bizcard.rulename,
      value: bizcard.rulename,
      ...bizcard
    }));
  });
</script>

<StudioForm
  bind:nextDisabled
  bind:step
  bind:selectedItem={selectedBizcard}
  {onExecute}
  {onRollback}
  mode="update"
  firstStepTitle={$format('label.SELECT_BUSINESS_CARD')}
  secondStepTitle={$format('label.UPDATE_BUSINESS_CARD')}
  executeLabel="label.UPDATE_BUSINESS_CARD"
  onLoadItem={loadBizcardItem}
  onExecuteAction={executeUpdateBizcard}
  onRollbackAction={rollbackUpdateBizcard}
>
  <div slot="selection">
    <Section>
      <SectionItem
        label={$format('label.BUSINESS_CARD')}
        description={$format('label.SELECT_BUSINESS_CARD_TO_UPDATE')}
        htmlFor="bizcardSelect"
      >
        <Select
          id="bizcardSelect"
          items={bizcardItems}
          value={selectedBizcard}
          on:change={handleBizcardSelect}
          placeholder={$format('label.SELECT_A_BUSINESS_CARD')}
          searchable
          showChevron
        >
          <div slot="empty" class="empty">
            {$format('label.NO_OPTIONS_AVAILABLE')}
          </div>
        </Select>
      </SectionItem>
    </Section>
  </div>
</StudioForm>

<style lang="scss">
  .empty {
    color: var(--color-gray-600);
    padding: var(--padding-xlarge);
    text-align: center;
  }
</style>
